import { useSelector } from '@tanstack/react-store'
import commentOrderStore from '@/utils/stores/commentOrder'
import type { Post } from '@/@types/post'
import type { CommentData } from '@/components/Comment/Comment'
import { CommentActionsProvider } from '@/components/Comment/CommentActionsContext'
import CommentForm from '@/components/Comment/CommentForm'
import {
	DELETE_COMMENT_MUTATION_OPTIONS,
	LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS,
	PIN_COMMENT_TOGGLE_MUTATION_OPTIONS,
} from '@/components/Comment/hooks'
import { ReplyToComment } from '@/components/Comment/schema'
import { showCommentFormModal } from '@/components/Comment/utils'
import NotFoundPage from '@/components/NotFoundPage'
import CommentListSection from '@/components/Post/CommentListSection'
import { PostActionsProvider } from '@/components/Post/PostActionsContext'
import PostCard from '@/components/Post/PostCard'
import {
	generateCommentQueryKey,
	generatePrimaryCommentsQueryKey,
	getPrimaryCommentsQueryOptions,
} from '@/utils/fetchers/comment'
import { prepareToRefetchListQuery, prependToInfiniteData } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import { Loader } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { flatMap, map, pick } from 'lodash-es'
import { HTMLMotionProps, motion } from 'motion/react'
import { RefObject, Suspense, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTitle } from 'react-use'
import AutoLoadMoreIndicator from '../AutoLoadMoreIndicator'
import styles from './PostDrawerContent.module.css'

export interface PostDrawerContentProps extends HTMLMotionProps<'div'> {
	post: Post

	scrollAreaRef?: RefObject<HTMLDivElement | null>
}

export default function PostDrawerContent({
	post,
	scrollAreaRef,
	...props
}: PostDrawerContentProps) {
	const hash = useLocation({
		select: (location) => {
			return location.hash
		},
	})
	const alignCommentListSectionAnchorRef = useRef<HTMLDivElement>(null)

	const order = useSelector(commentOrderStore)

	const { data, fetchNextPage, refetch, isFetchingNextPage } = useInfiniteQuery(
		getPrimaryCommentsQueryOptions({
			targetId: post.id,
			targetType: post.type,
			order,
		})
	)
	const refresh = useCallback(() => {
		prepareToRefetchListQuery(
			generatePrimaryCommentsQueryKey({
				targetId: post.id,
				targetType: post.type,
				order,
			})
		)
		return refetch()
	}, [refetch, post, order])

	const comments = useMemo(() => {
		return map(
			flatMap(data?.pages, (page) => page.data),
			(item) => pick(item, ['id', 'targetType']) as CommentData
		)
	}, [data])

	const isLocateToComments = hash === 'comments' && comments.length > 0

	const { mutateAsync: toggleLike } = useMutation(LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS)
	const { mutateAsync: togglePin } = useMutation(PIN_COMMENT_TOGGLE_MUTATION_OPTIONS)
	const { mutateAsync: deleteComment } = useMutation(DELETE_COMMENT_MUTATION_OPTIONS)
	const handleCommentReply = (toComment: ReplyToComment) => {
		const modalId = showCommentFormModal({
			replyToComment: toComment,
			onClose: () => {
				modals.close(modalId)
			},
			onSubmit: refresh,
		})
	}

	useTitle(`${post.user?.screenName}：${post.content} - 即刻`, {
		restoreOnUnmount: true,
	})

	useEffect(() => {
		if (isLocateToComments) {
			setTimeout(() => {
				alignCommentListSectionAnchorRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			}, 500)
		}
	}, [isLocateToComments])

	return (
		<motion.div {...props}>
			{post.deleted ? (
				<NotFoundPage />
			) : (
				<div className={styles.container}>
					<PostActionsProvider
						value={{
							onComment: (post) => {
								const modalId = showCommentFormModal({
									replyToPost: post,
									onClose: () => {
										modals.close(modalId)
									},
									onSubmit: refresh,
								})
							},
						}}
					>
						<PostCard mode="full" data={post} className={styles.postCard} />
					</PostActionsProvider>

					<Suspense fallback={<Loader />}>
						<CommentForm
							targetType={post.type}
							targetId={post.id}
							onSubmit={(newComment) => {
								queryClient.setQueryData(
									generateCommentQueryKey(pick(newComment, ['id', 'targetType'])),
									newComment
								)
								prependToInfiniteData(
									generatePrimaryCommentsQueryKey({
										targetId: post.id,
										targetType: post.type,
										order,
									}),
									newComment
								)
							}}
							className={styles.commentForm}
						/>
					</Suspense>

					<section className={styles.section}>
						<div ref={alignCommentListSectionAnchorRef} className={styles.anchor} />

						<Suspense fallback={<Loader className={styles.loader} />}>
							<CommentActionsProvider
								value={{
									refresh,
									onDelete: deleteComment,
									onToggleLike: toggleLike,
									onTogglePin: togglePin,
									onComment: handleCommentReply,
								}}
							>
								<CommentListSection data={comments} />
							</CommentActionsProvider>
						</Suspense>
					</section>
				</div>
			)}
			<AutoLoadMoreIndicator
				isLoading={isFetchingNextPage}
				onLoadMore={async () => void (await fetchNextPage())}
				container={scrollAreaRef}
			/>
		</motion.div>
	)
}
